import { useActiveTenant } from "../tenant/TenantContext";
import { useTenantQuery } from "../hooks/useTenantData";
import { ROLE_LABELS, occupancyValidity } from "../api/types";
import { ErrorState, LoadingState, PageHeader, Panel, Stat } from "../components/ui";

export function DashboardPage() {
  const membership = useActiveTenant();
  const profile = useTenantQuery(["profile"], (api, t) => api.getProfile(t));
  const blocks = useTenantQuery(["blocks"], (api, t) => api.listBlocks(t));
  const units = useTenantQuery(["units"], (api, t) => api.listUnits(t));
  const people = useTenantQuery(["people"], (api, t) => api.listPeople(t));
  const occupancies = useTenantQuery(["occupancies"], (api, t) => api.listOccupancies(t));
  const invitations = useTenantQuery(["invitations"], (api, t) => api.listInvitations(t));
  const devices = useTenantQuery(["devices"], (api, t) => api.listDevices(t));

  const queries = [blocks, units, people, occupancies, invitations, devices];
  const loading = queries.some((q) => q.isPending) || profile.isPending;
  const failed = queries.find((q) => q.isError) ?? (profile.isError ? profile : undefined);

  const pendingInvitations = invitations.data?.filter((i) => i.status === "pending").length ?? 0;
  const activeDevices = devices.data?.filter((d) => d.active).length ?? 0;
  const activeOccupancies =
    occupancies.data?.filter((o) => occupancyValidity(o) === "active").length ?? 0;

  const checklist: { label: string; done: boolean }[] = [
    { label: "Completar os dados cadastrais do condomínio", done: Boolean(profile.data?.address && profile.data?.timezone) },
    { label: "Cadastrar blocos ou torres", done: (blocks.data?.length ?? 0) > 0 },
    { label: "Cadastrar unidades", done: (units.data?.length ?? 0) > 0 },
    { label: "Cadastrar pessoas", done: (people.data?.length ?? 0) > 0 },
    { label: "Vincular pessoas às unidades (ocupações)", done: activeOccupancies > 0 },
    { label: "Enviar convites do App AVA", done: (invitations.data?.length ?? 0) > 0 },
    { label: "Ter ao menos um aparelho ativo", done: activeDevices > 0 },
  ];

  return (
    <>
      <PageHeader
        title={membership.tenant_name}
        subtitle={`Você está conectado como ${ROLE_LABELS[membership.role]}.`}
      />
      {loading && <LoadingState label="Carregando o resumo do condomínio…" />}
      {!loading && failed && <ErrorState error={failed.error} onRetry={() => failed.refetch()} />}
      {!loading && !failed && (
        <>
          <div className="stats">
            <Stat label="Blocos" value={blocks.data?.length ?? 0} />
            <Stat label="Unidades" value={units.data?.length ?? 0} />
            <Stat label="Pessoas" value={people.data?.length ?? 0} />
            <Stat label="Ocupações vigentes" value={activeOccupancies} />
            <Stat label="Convites pendentes" value={pendingInvitations} />
            <Stat label="Aparelhos ativos" value={activeDevices} />
          </div>
          <Panel title="Checklist de onboarding">
            {checklist.every((item) => item.done) ? (
              <p className="success">Onboarding completo: o condomínio está pronto para operar com o App AVA.</p>
            ) : (
              <p>Complete as etapas abaixo para deixar o condomínio operacional.</p>
            )}
            <ul className="checklist">
              {checklist.map((item) => (
                <li key={item.label} className={item.done ? "done" : ""}>
                  <span className="mark" aria-hidden="true">{item.done ? "✓" : "•"}</span>
                  {item.label}
                </li>
              ))}
            </ul>
          </Panel>
        </>
      )}
    </>
  );
}
